import { ALL_PUBLIC_ID_RESOURCE_TYPES, type PublicIdResourceType } from "./public-id";

// Lives here, not in `@quovy/server`: that package depends on `@quovy/agents`,
// and the agent tools validate against the same lists as the API routes.

/** Every `resource_type` a write may declare. */
export const LINKED_RESOURCE_TYPES = [
  "task",
  "custom_record",
  "workflow_run",
  "workflow_definition",
  "extraction_run",
  "classification_run",
  "processor_definition",
  "agent",
  "agent_thread",
  "file",
  "upload",
  "pipeline",
  "inbound_email",
] as const;

export type LinkedResourceType = (typeof LINKED_RESOURCE_TYPES)[number];

/** Retired types: unwritable, but stored rows still carry them so filters must accept them. */
export const LEGACY_LINKED_RESOURCE_TYPES = ["record"] as const;

export type LinkedResourceStoredType =
  | PublicIdResourceType
  | LinkedResourceType
  | (typeof LEGACY_LINKED_RESOURCE_TYPES)[number];

// Every value a stored row may CARRY — wider than a write accepts, because an
// `x-quovy-ref` field may target any registered object.
export const LINKED_RESOURCE_STORED_TYPES = [
  ...new Set<LinkedResourceStoredType>([
    ...ALL_PUBLIC_ID_RESOURCE_TYPES,
    ...LINKED_RESOURCE_TYPES,
    ...LEGACY_LINKED_RESOURCE_TYPES,
  ]),
] as [LinkedResourceStoredType, ...LinkedResourceStoredType[]];

/** Every `source_type` value the table holds, including rows only readable generically. */
export const LINKED_RESOURCE_SOURCE_TYPES = [
  "task",
  "agent",
  "workflow_run",
  "inbound_email",
  "custom_record",
] as const;

export type LinkedResourceSourceType = (typeof LINKED_RESOURCE_SOURCE_TYPES)[number];

// `custom_record` rows are owned transactionally by the custom-model domain
// (reference fields), so no generic write may create or delete them.
export const LINKED_RESOURCE_WRITABLE_SOURCE_TYPES = [
  "task",
  "agent",
  "workflow_run",
  "inbound_email",
] as const;

export type LinkedResourceWritableSourceType =
  (typeof LINKED_RESOURCE_WRITABLE_SOURCE_TYPES)[number];

// Shared `linked_resources.purpose` presets. UI selectors render these plus a
// "custom" entry; the column itself accepts any trimmed, ≤64-char string —
// this list is suggestions, not a closed enum.
export const LINKED_RESOURCE_PURPOSE_PRESETS = ["source_sop", "reference", "attachment"] as const;

export type LinkedResourcePurposePreset = (typeof LINKED_RESOURCE_PURPOSE_PRESETS)[number];

export const LINKED_RESOURCE_PURPOSE_MAX_LENGTH = 64;

/**
 * Rows per bulk linked-resource request. Each row takes its own SAVEPOINT and,
 * for a task source, a row lock, so the cap bounds how long one transaction
 * holds those locks rather than the payload size.
 */
export const LINKED_RESOURCE_BULK_MAX_ITEMS = 200;

/** Where a row came from. A `custom_record_field` row is DERIVED from
 *  `custom_records.data` and has no link row, so no `id` to address it by. */
export const LINKED_RESOURCE_ORIGINS = ["link", "custom_record_field"] as const;

export type LinkedResourceOrigin = (typeof LINKED_RESOURCE_ORIGINS)[number];

/** One row from `/v1/linked-resources`, `/v1/agents/:agentId/linked-resources` or the task equivalent — same wire shape everywhere. */
export type LinkedResource = {
  /** Null on a `custom_record_field` row: nothing addresses a derived row. */
  id: string | null;
  origin: LinkedResourceOrigin;
  /** The record's reference field names on a `custom_record_field` row, else null. */
  source_fields: string[] | null;
  source_type: string;
  source_id: string;
  source_name: string | null;
  resource_type: string;
  resource_id: string;
  resource_name: string | null;
  label: string | null;
  purpose: string | null;
  metadata_json: Record<string, unknown>;
  created_at: string;
};

export type DashboardLinkedResource = LinkedResource & {
  source_model_name: string | null;
  resource_model_name: string | null;
};

export type AgentLinkedResource = LinkedResource;

/**
 * Stable per-row key for React lists and client-side dedupe. A derived row has
 * no `id`, and its (source, target) tuple is unique by construction — this is a
 * render key, never something the API will accept back.
 */
export function linkedResourceKey(resource: {
  id: string | null;
  source_type: string;
  source_id: string;
  resource_type: string;
  resource_id: string;
}): string {
  return (
    resource.id ??
    `${resource.source_type}:${resource.source_id}:${resource.resource_type}:${resource.resource_id}`
  );
}
